/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { AppStore } from 'src/core/stores/app/AppStore';
import { OmitFunctions } from 'src/core/stores/types';
import { JobCreateSwagger } from 'src/customText/core/services/jobsSwagger';
import { Language } from 'src/languageStudio/appShell/prebuiltDemos/utils/supportedLanguages';
import { isDocumentSwaggerRtl } from 'src/languageStudio/core/utils/demosUtils';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { QnaMakerQnaSwagger, QnaMakerSynonymSwagger } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerMetricStore } from 'src/qnaMaker/core/stores/QnaMakerMetricStore';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';
import { QnaMakerLanguage, QnaMakerLanguageDirection } from 'src/qnaMaker/core/types/QnaMakerLanguages';
import { QnaMakerPredictedQnA } from 'src/qnaMaker/core/types/QnaMakerPredictedQnA';
import { qnaJsonToQnaRowMetricStore } from 'src/qnaMaker/core/utils/qnaMetricUtils';

export class QnaMakerProjectStore extends AppStore {
    public readonly id: string;

    public sourcesStoreHouse = createResourceStoreHouse<QnaMakerSourceStore>();

    public testPanePredictionStoreHouse = createResourceStoreHouse<QnaMakerPredictedQnA>();

    @observable public name: string;

    @observable public isSmartSuggestionEnabled: boolean = true;

    @observable public description: string;

    @observable public defaultAnswer: string;

    @observable public language: QnaMakerLanguage;

    @observable public areQnaPairsLoaded: boolean;

    @observable public multilingualResource: boolean;

    @observable.shallow public qnasJsonData: QnaMakerQnaSwagger[];

    @observable public publishStatus: JobCreateSwagger<object>['status'];

    @observable.shallow public synonymsJsonData: QnaMakerSynonymSwagger[];

    @observable public areSynonymsLoaded: boolean;

    @observable public qnaMetricStore: QnaMakerMetricStore;

    constructor(id: string, data: Partial<OmitFunctions<QnaMakerProjectStore>>) {
        super(id, 'qnaMaker', data);

        this.qnasJsonData = [];
        this.areQnaPairsLoaded = false;
        this.synonymsJsonData = [];
        this.areSynonymsLoaded = false;
        this.language = data.language;
        this.publishStatus = 'notStarted';
        this.defaultAnswer = data.defaultAnswer;
        this.qnaMetricStore = new QnaMakerMetricStore(id);
        this.multilingualResource = data.multilingualResource;
    }

    @computed get haveSmartSuggestions() {
        return this.qnasJsonData.some(qna => qna.activeLearningSuggestions.some(a => a.totalAutoSuggestionCountFromGenerativeModel));
    }

    @computed
    public get languageDirection(): QnaMakerLanguageDirection {
        return isDocumentSwaggerRtl(this.language as Language['code']) ? 'rtl' : 'ltr';
    }

    @action
    public setSynonymsJsonData(data: QnaMakerSynonymSwagger[]) {
        this.synonymsJsonData = [...data];
        this.areSynonymsLoaded = true;
    }

    @action
    public setIsSmartSuggestionsEnabled(isEnabled: boolean) {
        this.isSmartSuggestionEnabled = isEnabled;
    }

    @action
    public setQnasJsonData(data: QnaMakerQnaSwagger[]) {
        this.qnasJsonData = [...data];
        this.areQnaPairsLoaded = true;
        this.qnaMetricStore.setRowMetrics(qnaJsonToQnaRowMetricStore(this.qnasJsonData));
    }

    @action
    public resetQnaPairsLoadedState() {
        this.areQnaPairsLoaded = false;
    }

    @action
    public resetSynonymsLoadedState() {
        this.areSynonymsLoaded = false;
    }

    @action
    public setPublishStatus(status: JobCreateSwagger<object>['status']) {
        this.publishStatus = status;
    }

    @action
    public setDescription(description: string) {
        this.description = description;
    }

    @action
    public setDefaultAnswer(defaultAnswer: string) {
        this.defaultAnswer = defaultAnswer;
    }
}
